import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { Radar, MapPin } from 'lucide-react'
import { useWorkers } from '@/hooks/data/useWorkers'
import { connectWorkerHub, type LocationUpdatedPayload } from '@/api/common/WorkerHubClient'
import { tokenStore } from '@/api/tokenStore'

interface LiveEntry extends LocationUpdatedPayload {
  receivedAt: number;
}

function timeAgo(ms: number, now: number): string {
  const seconds = Math.max(0, Math.round((now - ms) / 1000))
  if (seconds < 60) return `${seconds}s ago`
  return `${Math.round(seconds / 60)}m ago`
}

function LiveWorkforcePage() {
  const { data: workers } = useWorkers()
  const [positions, setPositions] = useState<Record<string, LiveEntry>>({})
  const [connected, setConnected] = useState(false)
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 5000)
    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    const token = tokenStore.get()?.accessToken
    if (!token) return

    const onLocationUpdated = (payload: LocationUpdatedPayload) => {
      setPositions((prev) => ({ ...prev, [payload.workerId]: { ...payload, receivedAt: Date.now() } }))
    }
    const connection = connectWorkerHub(token, { onLocationUpdated })
    connection.onreconnected(() => setConnected(true))
    connection.onclose(() => setConnected(false))

    // connectWorkerHub fires connection.start() without exposing the promise, and
    // SignalR's HubConnection has no "initial connect succeeded" event (onreconnected
    // only covers reconnects after a prior successful connection) — so the only way to
    // reflect the real outcome is to poll .state until it leaves "Connecting".
    const pollId = setInterval(() => {
      if (connection.state === 'Connected') {
        setConnected(true)
        clearInterval(pollId)
      } else if (connection.state === 'Disconnected') {
        setConnected(false)
        clearInterval(pollId)
      }
    }, 300)

    return () => {
      clearInterval(pollId)
      connection.stop().catch(() => {})
    }
  }, [])

  const nameFor = (workerId: string) => workers?.find((w) => w.id === workerId)?.name ?? workerId.slice(0, 8)
  const entries = Object.values(positions).sort((a, b) => b.receivedAt - a.receivedAt)

  return (
    <div className="p-2 md:p-4 space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
          <Radar className="w-6 h-6" /> Live Workforce
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Real-time worker positions over WorkerHub&apos;s <code>OnLocationUpdated</code>. Nothing
          is persisted server-side — this only shows workers who have pinged since you opened this
          page. There&apos;s no map view yet (no map library is installed in this app); positions
          are shown as coordinates.
        </p>
        <p className="mt-1 text-xs">
          <span className={`inline-flex items-center gap-1 ${connected ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${connected ? 'bg-green-500' : 'bg-gray-400'}`} />
            {connected ? 'Connected' : 'Connecting…'}
          </span>
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {entries.length === 0 ? (
          <p className="p-4 text-sm text-gray-500">
            No positions yet — a worker checked into a job will start pinging their location automatically.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 text-left text-xs uppercase text-gray-500 dark:text-gray-400">
              <tr>
                <th className="px-4 py-2">Worker</th>
                <th className="px-4 py-2">Position</th>
                <th className="px-4 py-2">Last seen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {entries.map((entry) => (
                <tr key={entry.workerId}>
                  <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{nameFor(entry.workerId)}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                    <span className="flex items-center gap-1">
                      <MapPin className="h-3.5 w-3.5" /> {entry.latitude.toFixed(5)}, {entry.longitude.toFixed(5)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-500">{timeAgo(entry.receivedAt, now)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

export const Route = createFileRoute('/(admin)/live-workforce/')({ component: LiveWorkforcePage })
